/**
 * Shared Cognito token storage for theme login and Shopping Assistant bubble.
 * Theme login writes tokens after /apps/custom-events sign-in; bubble reads them.
 * Falls back to Cognito USER_PASSWORD_AUTH when custom-events omits cognito_tokens.
 */
;(function () {
  var STORAGE_KEY = "tb-cognito-user-tokens"
  var DEFAULT_REGION = "us-west-2"

  function parseJwtExp(idToken) {
    try {
      var part = String(idToken || "").split(".")[1]
      if (!part) return null
      part = part.replace(/-/g, "+").replace(/_/g, "/")
      while (part.length % 4) part += "="
      var payload = JSON.parse(atob(part))
      return payload && payload.exp ? Number(payload.exp) : null
    } catch (e) {
      return null
    }
  }

  function normalizeTokens(raw) {
    if (!raw || !raw.idToken) return null
    // Prefer id-token JWT exp. Cognito ExpiresIn is access-token lifetime and
    // is often shorter than the id token used for Identity Pool exchange.
    var exp = parseJwtExp(raw.idToken)
    var expiresAt = exp ? exp * 1000 : raw.expiresAt
    if (!expiresAt && raw.expiresIn) {
      expiresAt = Date.now() + Number(raw.expiresIn) * 1000
    }
    if (!expiresAt) {
      expiresAt = Date.now() + 55 * 60 * 1000
    }
    return {
      idToken: raw.idToken,
      accessToken: raw.accessToken || null,
      refreshToken: raw.refreshToken || null,
      expiresAt: Number(expiresAt),
    }
  }

  function dispatchChange(detail) {
    try {
      window.dispatchEvent(
        new CustomEvent("tb-auth-tokens-changed", { detail: detail || null }),
      )
    } catch (e) {
      // ignore
    }
  }

  function uniqueIds(ids) {
    var out = []
    var seen = {}
    ;(ids || []).forEach(function (id) {
      var v = String(id || "").trim()
      if (!v || seen[v]) return
      seen[v] = true
      out.push(v)
    })
    return out
  }

  function getConfig() {
    var cfg = window.__TB_AUTH_CONFIG__ || {}
    var clientIds = uniqueIds(
      [].concat(cfg.clientIds || [], cfg.clientId || []),
    )
    return {
      region: (cfg.region || DEFAULT_REGION).trim(),
      clientIds: clientIds,
    }
  }

  async function initiateAuth(email, password, clientId, region) {
    var res = await fetch("https://cognito-idp." + region + ".amazonaws.com/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }),
    })
    var data = await res.json().catch(function () {
      return {}
    })
    if (!res.ok) {
      var msg =
        data.message || data.__type || "Cognito InitiateAuth HTTP " + res.status
      var err = new Error(msg)
      err.clientId = clientId
      err.status = res.status
      err.payload = data
      throw err
    }
    var auth = data.AuthenticationResult || {}
    if (!auth.IdToken) {
      throw new Error("Cognito InitiateAuth returned no IdToken")
    }
    return {
      idToken: auth.IdToken,
      accessToken: auth.AccessToken || null,
      refreshToken: auth.RefreshToken || null,
      expiresIn: auth.ExpiresIn,
    }
  }

  function clearStoredTokens() {
    localStorage.removeItem(STORAGE_KEY)
    dispatchChange(null)
  }

  // Shopify logout does not clear theme localStorage. Clear Cognito tokens on
  // logout navigation and whenever the storefront session is already guest.
  function bindLogoutTokenClear() {
    document.addEventListener(
      "click",
      function (event) {
        var el = event.target
        while (el && el !== document && el.tagName !== "A") {
          el = el.parentNode
        }
        if (!el || el.tagName !== "A") return
        var href = el.getAttribute("href") || ""
        if (href.indexOf("/account/logout") !== -1) {
          clearStoredTokens()
        }
      },
      true,
    )
  }

  function clearTokensIfLoggedOut() {
    try {
      var customer = window.Shopify && window.Shopify.Customer
      // Only act when Customer was populated by the theme (id "" / missing ⇒ guest).
      if (!customer || customer.isLoggedIn) return
      if (localStorage.getItem(STORAGE_KEY)) {
        clearStoredTokens()
      }
    } catch (e) {
      // ignore
    }
  }

  window.TBAuth = {
    storageKey: STORAGE_KEY,
    getTokens: function () {
      try {
        var raw = localStorage.getItem(STORAGE_KEY)
        return raw ? normalizeTokens(JSON.parse(raw)) : null
      } catch (e) {
        return null
      }
    },
    getIdToken: function () {
      var tokens = this.getTokens()
      return tokens && tokens.idToken ? tokens.idToken : null
    },
    setTokens: function (tokens) {
      var normalized = normalizeTokens(tokens)
      if (!normalized) return
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized))
      dispatchChange(normalized)
    },
    clearTokens: function () {
      clearStoredTokens()
    },
    /**
     * Direct Cognito password auth. Tries configured public app clients until
     * one accepts USER_PASSWORD_AUTH. Pass exclusiveClients:true with clientId(s)
     * to skip falling back to other cfg.clientIds (needed for SA Identity Pool).
     */
    signInWithPassword: async function (email, password, options) {
      options = options || {}
      var cfg = getConfig()
      var region = (options.region || cfg.region || DEFAULT_REGION).trim()
      var preferred = [].concat(options.clientIds || [], options.clientId || [])
      var clientIds = uniqueIds(
        options.exclusiveClients ? preferred : preferred.concat(cfg.clientIds),
      )
      if (!clientIds.length) {
        throw new Error(
          "Missing Cognito clientId for TBAuth.signInWithPassword",
        )
      }
      if (!email || !password) {
        throw new Error("Missing email/password for TBAuth.signInWithPassword")
      }

      var errors = []
      for (var i = 0; i < clientIds.length; i++) {
        var clientId = clientIds[i]
        try {
          var tokens = await initiateAuth(email, password, clientId, region)
          this.setTokens(tokens)
          var stored = this.getTokens()
          if (!stored || !stored.idToken) {
            throw new Error(
              "InitiateAuth succeeded but tokens missing from localStorage",
            )
          }
          console.info("[TBAuth] Cognito tokens stored via client", clientId)
          return stored
        } catch (e) {
          errors.push({
            clientId: clientId,
            message: (e && e.message) || String(e),
          })
        }
      }
      console.warn(
        "[TBAuth] Cognito InitiateAuth failed for all clients",
        errors,
      )
      throw new Error(
        "Cognito InitiateAuth failed for all clients: " +
          errors
            .map(function (e) {
              return e.clientId + " (" + e.message + ")"
            })
            .join("; "),
      )
    },
  }

  bindLogoutTokenClear()
  clearTokensIfLoggedOut()
})()
